import React, {
  useEffect, useRef, useState, useContext,
} from 'react';
import ProductPageContext from '../../contexts/ProductPageContext';

export default function SizeTables({ table, region, measurement }) {
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [stickyColumnWidth, setStickyColumnWidth] = useState(0);
  const scrollContainerRef = useRef(null);
  const ProductPageState = useContext(ProductPageContext);
  const {
    activeProduct,
  } = ProductPageState.collectionData;

  const updateShadows = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const stickyColumn = container.querySelector('.mfe-size-guide__primary-column');
    if (stickyColumn) {
      setStickyColumnWidth(stickyColumn.offsetWidth);
    }

    const { scrollLeft } = container;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    setShowLeftShadow(scrollLeft > 0);
    setShowRightShadow(scrollLeft < maxScrollLeft);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (container) {
      container.addEventListener('scroll', updateShadows);
      updateShadows();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', updateShadows);
      }
    };
  }, [region]);

  useEffect(() => {
    window.addEventListener('resize', updateShadows);
    return () => window.removeEventListener('resize', updateShadows);
  }, []);

  const renderData = (row, isRowHeader) => row.map((data, index) => {
    let Tag = 'td';

    if (isRowHeader) {
      Tag = 'th';
    }

    if ((data.country === '' && data.unit === '')
      || data.country === region
      || data.unit === measurement.unit
      || data.unit === measurement.weight) {
      return (
        // Using dangerouslySetInnerHTML to force HTML entities to render properly
        <Tag
          key={`${data.unit}-${data.country}-${index}`}
          className={`size-tables__cell ${(isRowHeader && index === 0)
            ? 'mfe-size-guide__primary-column' : ''}`}
          dangerouslySetInnerHTML={{ __html: data.value }}
        />
      );
    }
    return null;
  });

  const renderHead = (row) => (
    <tr className="size-tables__row mfe-alt-background-color">
      {row.map(({ values }) => values.map((rowData) => renderData(rowData, true)))}
    </tr>
  );

  const renderRows = (rows) => rows.map((row, rowIndex) => (
    row.map(({ values }, valueIndex) => values.map((rowData, index) => (
      <tr
        className={`${((rowIndex + Math.floor(valueIndex / 2)) % 2 === 0)
          ? '' : 'mfe-alt-background-color'}`}
        key={index}
      >
        {index === 0 && (
        <th
          className={`size-tables__cell ${index === 0 ? 'mfe-size-guide__primary-column' : ''}`}
          rowSpan={row[0].values.length}
        >
          {row[0].size}
        </th>
        )}
        {renderData(rowData)}
      </tr>
    )))
  ));

  if (!table) return null;
  const tableData = [...table.tables];
  const headerRow = tableData.shift();

  const getTitle = () => {
    const brandsToUppercase = ['hol', 'gh'];
    return brandsToUppercase.includes(activeProduct.brand) ? table.title.toUpperCase() : table.title;
  };

  return (
    <div className="size-tables">
      <h3 className="size-tables__title h2" data-product-brand={activeProduct.brand}>
        {getTitle()}
      </h3>
      <p className="size-tables__description">{table.description}</p>

      <div className="tableContainerWithShadows">
        {showLeftShadow
        && (
        <div
          className="mfe-size-guide__shadow mfe-size-guide__shadow--left"
          style={{ left: `${stickyColumnWidth}px` }}
        />
        )}
        <div className="mfe-size-guide__table-scroll" ref={scrollContainerRef} data-product-brand={activeProduct.brand}>
          <table className="size-tables__table" cellSpacing="0">
            <thead>
              {renderHead(headerRow)}
            </thead>
            <tbody>
              {renderRows(tableData)}
            </tbody>
          </table>
        </div>
        {showRightShadow
        && (
        <div className="mfe-size-guide__shadow mfe-size-guide__shadow--right" />
        )}
      </div>
    </div>
  );
}
